<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KanbanAPI from 'dashboard/api/kanban';

const props = defineProps({
  pipelineId: { type: Number, required: true },
});

const store = useStore();
const { t } = useI18n();

const cards = ref([]);
const isLoading = ref(false);
const restoringId = ref(null);

async function fetchTrash() {
  isLoading.value = true;
  try {
    const { data } = await KanbanAPI.getTrash(props.pipelineId);
    cards.value = data.data;
  } catch {
    useAlert(t('KANBAN.TRASH.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

async function restore(card) {
  restoringId.value = card.id;
  try {
    await KanbanAPI.restoreCard(card.id);
    cards.value = cards.value.filter(c => c.id !== card.id);
    useAlert(t('KANBAN.TRASH.RESTORED'));
    store.dispatch('kanban/fetchCards');
  } catch {
    useAlert(t('KANBAN.TRASH.RESTORE_ERROR'));
  } finally {
    restoringId.value = null;
  }
}

function formatDate(timestamp) {
  return new Date(timestamp * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

watch(() => props.pipelineId, fetchTrash, { immediate: true });
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-sm text-n-slate-11">
      {{ t('KANBAN.TRASH.DESCRIPTION') }}
    </p>

    <p v-if="isLoading" class="text-sm text-n-slate-10">
      <span class="i-lucide-loader-circle size-4 animate-spin" />
    </p>

    <p
      v-else-if="!cards.length"
      class="px-3 py-6 text-sm text-center text-n-slate-10 italic"
    >
      {{ t('KANBAN.TRASH.EMPTY') }}
    </p>

    <ul v-else class="flex flex-col divide-y divide-n-weak">
      <li
        v-for="card in cards"
        :key="card.id"
        class="flex items-center gap-3 py-2.5"
      >
        <div class="flex flex-col min-w-0 flex-1">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ card.title || card.contact_name }}
          </span>
          <span class="text-xs text-n-slate-10 truncate">
            {{
              t('KANBAN.TRASH.DELETED_AT', {
                column: card.column_name,
                date: formatDate(card.deleted_at),
              })
            }}
            <template v-if="card.deleted_by_name">
              {{ t('KANBAN.TRASH.DELETED_BY', { name: card.deleted_by_name }) }}
            </template>
          </span>
        </div>
        <button
          class="flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-12 bg-n-background hover:bg-n-alpha-1 transition-colors disabled:opacity-50 flex-shrink-0"
          :disabled="restoringId === card.id"
          @click="restore(card)"
        >
          <span class="i-lucide-undo-2 size-4" />
          {{ t('KANBAN.TRASH.RESTORE') }}
        </button>
      </li>
    </ul>
  </div>
</template>
